import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, TouchableOpacity, ScrollView } from 'react-native';
import Toast from 'react-native-toast-message';
import { StyleSheet } from 'react-native-unistyles';
import SheetModal from '@/components/General/SheetModal';
import SelectInput from '@/components/General/inputs/SelectInput';
import Input from '@/components/General/inputs/Input';
import ToggleRow from '@/components/General/inputs/ToggleRow';
import Unicon from '@/components/General/Unicon';
import { Query } from '@/utils/react-query/Queries';
import { AUTOMATION_DUE_TYPES } from '@/utils/constants/taskTypes';
import {
    groupTriggersByFamily, triggerAllowsScope,
    buildTriggerConfig, buildInputSchema,
} from '@/utils/automationBuilder';

const INPUT_FIELD_TYPE_OPTIONS = [
    { value: 'string', label: 'Text' },
    { value: 'number', label: 'Number' },
    { value: 'boolean', label: 'Yes/No' },
    { value: 'uuid', label: 'ID' },
    { value: 'datetime', label: 'Date & time' },
];

const INPUT_SEMANTIC_OPTIONS = [
    { value: '', label: 'None' },
    { value: 'personID', label: 'Person ID' },
    { value: 'taskInstanceID', label: 'Task Instance ID' },
    { value: 'followUpEnrollmentID', label: 'Follow Up Enrollment ID' },
];

/**
 * TriggerConfigSheet — the v2 source-aware trigger editor (§14.3/§14.4).
 * Grouped trigger family picker → specific event → configured source where
 * the catalog requires one (formID) plus optional scopes (followUpPathID /
 * followUpAudienceID / taskID / roleID per the validate_bindings matrix).
 * MANUAL/PATH_ACTION also edit the declared inputSchema.
 *
 * The sheet never writes a second trigger — the parent saves onto the ONE
 * existing TRIGGER PathStep. `incompatible` carries the post-save
 * validate_bindings errors so a trigger/source change reports exactly which
 * step bindings it broke (exit gate: report, never silently drop).
 */
const TriggerConfigSheet = ({
    isOpen, onClose, catalog = [],
    initialType, initialConfig = {}, initialInputSchema,
    onSave, saving,
    organizationID,
    showDefinitionFields = false,
}) => {
    const [triggerType, setTriggerType] = useState(initialType || null);
    const [source, setSource] = useState(initialConfig || {});
    const [inputFields, setInputFields] = useState(initialInputSchema?.fields || []);
    const [definition, setDefinition] = useState({ name: '', description: '' });

    useEffect(() => {
        if (isOpen) {
            setTriggerType(initialType || null);
            setSource(initialConfig || {});
            setInputFields(initialInputSchema?.fields || []);
        }
    }, [isOpen, initialType]);

    const catalogRow = useMemo(
        () => catalog.find(r => r.triggerType === triggerType) || null,
        [catalog, triggerType]);

    const groups = useMemo(() => groupTriggersByFamily(catalog), [catalog]);

    const triggerOptions = useMemo(() =>
        groups.flatMap((group, index) => [
            ...(index > 0 ? [{ separator: true }] : []),
            { section: group.label },
            ...group.triggers.map(t => ({
                value: t.triggerType,
                label: t.label,
                subtitle: `${t.description}${t.personOptional ? ' No person needed.' : ''}`,
            })),
        ]),
    [groups]);

    const { data: forms } = Query.forms.all({ options: { enabled: isOpen && triggerType === 'FORM_ENTRY' } });
    const { data: followUpPaths } = Query.followUp.paths({ organizationID, options: { enabled: isOpen && !!organizationID && !!triggerType?.startsWith('FOLLOW_UP') } });
    const { data: audiences } = Query.followUp.audiences({ organizationID, options: { enabled: isOpen && !!organizationID && !!triggerType?.startsWith('FOLLOW_UP') } });
    const { data: roles } = Query.roles.all({ options: { enabled: isOpen && triggerType === 'ATTENDANCE' } });
    const tasksQuery = Query.tasks.byDueType({ dueType: AUTOMATION_DUE_TYPES, options: { enabled: isOpen && !!triggerType?.startsWith('TASK_') } });
    const taskRows = tasksQuery?.data || [];

    const formOptions = useMemo(() =>
        (forms || []).filter(f => f.status !== 'archived').map(f => ({ value: f.id, label: f.title })),
    [forms]);

    const followUpPathOptions = useMemo(() =>
        (followUpPaths || []).filter(p => !p.isArchived).map(p => ({ value: p.pathID, label: p.name })),
    [followUpPaths]);

    const audienceOptions = useMemo(() =>
        (audiences || []).map(a => ({ value: a.audienceID || a.id, label: a.name })),
    [audiences]);

    const taskOptions = useMemo(() =>
        taskRows.map(t => ({ value: t.id, label: t.title })),
    [taskRows]);

    const roleOptions = useMemo(() =>
        (roles || []).map(r => ({ value: r.id, label: r.name })),
    [roles]);

    const setSourceKey = (key, val) => setSource(prev => ({ ...prev, [key]: val }));
    const isFollowUp = triggerType?.startsWith('FOLLOW_UP_');
    const isTaskTrigger = triggerType === 'TASK_COMPLETED' || triggerType === 'TASK_CANCELLED';
    const hasDeclaredInputs = triggerType === 'MANUAL' || triggerType === 'PATH_ACTION';

    const missingRequiredSource = catalogRow?.sourceParam && !source[catalogRow.sourceParam];

    const updateInputField = (idx, patch) => {
        setInputFields(prev => prev.map((f, i) => i === idx ? { ...f, ...patch } : f));
    };
    const removeInputField = (idx) => setInputFields(prev => prev.filter((_, i) => i !== idx));
    const addInputField = () => setInputFields(prev => [...prev, { key: '', label: '', type: 'string', required: false }]);

    const handleConfirm = () => {
        if (showDefinitionFields && !definition.name?.trim()) {
            Toast.show({ type: 'error', text2: 'Enter an automation name.' });
            return;
        }
        if (!triggerType) {
            Toast.show({ type: 'error', text2: 'Select a trigger first.' });
            return;
        }
        if (missingRequiredSource) {
            Toast.show({ type: 'error', text2: triggerType === 'FORM_ENTRY' ? 'Choose the form that starts runs.' : 'Choose the required source.' });
            return;
        }
        const triggerConfig = buildTriggerConfig(triggerType, catalogRow, source);
        const inputSchema = hasDeclaredInputs ? buildInputSchema(inputFields) : undefined;
        onSave?.(triggerType, triggerConfig, inputSchema, definition);
    };

    return (
        <SheetModal
            isOpen={isOpen}
            onCancel={onClose}
            onClose={onClose}
            title={initialType ? 'Edit Trigger' : 'Choose a Trigger'}
            cancelText="Cancel"
            confirmText="Save"
            onConfirm={handleConfirm}
            loading={saving}
        >
            <ScrollView style={styles.scroll} keyboardShouldPersistTaps="handled">
                {showDefinitionFields && (
                    <View style={{ marginBottom: 14 }}>
                        <Input
                            title="Automation Name"
                            placeholder="e.g. New visitor follow-up"
                            value={definition.name}
                            onUpdate={(val) => setDefinition(prev => ({ ...prev, name: val }))}
                        />
                        <Input
                            title="Description"
                            placeholder="What does this automation do?"
                            multiline
                            value={definition.description}
                            onUpdate={(val) => setDefinition(prev => ({ ...prev, description: val }))}
                        />
                    </View>
                )}

                {/* Trigger picker — family labeled in each option's subtitle */}
                <SelectInput
                    title="Trigger"
                    required
                    placeholder="Choose what starts runs..."
                    value={triggerType}
                    options={triggerOptions}
                    onUpdate={(val) => setTriggerType(val)}
                    hideClearButton
                    isNotOnSurface
                    icon="flash-outline"
                    testID="trigger-select"
                />

                {/* Configured source (§14.4) */}
                {triggerType === 'FORM_ENTRY' && (
                    <SelectInput
                        title="Form"
                        placeholder="Select the form that starts runs..."
                        value={source.formID}
                        options={formOptions}
                        onUpdate={(val) => setSourceKey('formID', val)}
                        required
                        helpText="Only this form's answers are available as variables. Changing the form invalidates field bindings."
                    />
                )}

                {triggerType === 'ATTENDANCE' && (
                    <View>
                        <SelectInput
                            title="Mode"
                            value={source.mode || 'MISSED'}
                            options={[
                                { value: 'MISSED', label: 'Missed weeks' },
                                { value: 'ATTENDED', label: 'Attendance milestone' },
                            ]}
                            onUpdate={(val) => setSourceKey('mode', val)}
                        />
                        <Input
                            title="Weeks"
                            placeholder="4"
                            keyboardType="numeric"
                            value={String(source.weeks || '')}
                            onUpdate={(val) => setSourceKey('weeks', val)}
                        />
                        <SelectInput
                            title="Role (optional)"
                            placeholder="Anyone in the organization"
                            value={source.roleID}
                            options={roleOptions}
                            onUpdate={(val) => setSourceKey('roleID', val)}
                        />
                    </View>
                )}

                {triggerType === 'EVENT_CHECK_IN' && (
                    <Text style={styles.hintText}>
                        Link events from each event instance&rsquo;s Check-in Automation setting. Runs capture the instance, attendance row, and attendee.
                    </Text>
                )}

                {triggerType === 'SCHEDULE' && (
                    <Text style={styles.hintText}>
                        Configure the recurrence from the automation&rsquo;s Manage Schedule button. Scheduled runs do not need a person.
                    </Text>
                )}

                {isFollowUp && (
                    <View>
                        {triggerAllowsScope(triggerType, 'followUpPathID') && (
                            <SelectInput
                                title="Follow Up Path (optional)"
                                placeholder="Any Follow Up path"
                                helpText="Limit this trigger to one Follow Up path template. Leave empty for all."
                                value={source.followUpPathID}
                                options={followUpPathOptions}
                                onUpdate={(val) => setSourceKey('followUpPathID', val)}
                            />
                        )}
                        {triggerAllowsScope(triggerType, 'followUpAudienceID') && (
                            <SelectInput
                                title="Audience (optional)"
                                placeholder="Any audience"
                                helpText="Limit this trigger to one Follow Up audience. Leave empty for all."
                                value={source.followUpAudienceID}
                                options={audienceOptions}
                                onUpdate={(val) => setSourceKey('followUpAudienceID', val)}
                            />
                        )}
                    </View>
                )}

                {isTaskTrigger && (
                    <SelectInput
                        title="Task (optional)"
                        placeholder="Any task"
                        helpText="Limit this trigger to one task definition. Leave empty for all tasks."
                        value={source.taskID}
                        options={taskOptions}
                        onUpdate={(val) => setSourceKey('taskID', val)}
                    />
                )}

                {/* Declared inputs for MANUAL / called automations (§16.1) */}
                {hasDeclaredInputs && (
                    <View style={styles.inputSchemaBlock}>
                        <Text style={styles.groupLabel}>Declared inputs</Text>
                        <Text style={styles.hintText}>
                            Values the caller provides when starting a run. Steps bind them as input.*.
                        </Text>
                        {inputFields.map((field, idx) => (
                            <View key={idx} style={styles.inputFieldRow} testID={`input-field-row-${idx}`}>
                                <View style={styles.inputFieldHeader}>
                                    <Text style={styles.inputFieldIndex}>Input {idx + 1}</Text>
                                    <TouchableOpacity hitSlop={10} onPress={() => removeInputField(idx)} testID={`input-field-remove-${idx}`}>
                                        <Unicon name="close-circle" size={18} colorVariant="error" />
                                    </TouchableOpacity>
                                </View>
                                <View style={styles.inputFieldCols}>
                                    <View style={styles.inputFieldCol}>
                                        <Input
                                            title="Key"
                                            placeholder="e.g. notes"
                                            value={field.key || ''}
                                            onUpdate={(val) => updateInputField(idx, { key: val.replace(/[^a-zA-Z0-9_]/g, ''), label: field.label || val })}
                                        />
                                    </View>
                                    <View style={styles.inputFieldCol}>
                                        <Input
                                            title="Label"
                                            placeholder="Shown in pickers"
                                            value={field.label || ''}
                                            onUpdate={(val) => updateInputField(idx, { label: val })}
                                        />
                                    </View>
                                </View>
                                <View style={styles.inputFieldCols}>
                                    <View style={styles.inputFieldCol}>
                                        <SelectInput
                                            title="Type"
                                            value={field.type || 'string'}
                                            options={INPUT_FIELD_TYPE_OPTIONS}
                                            onUpdate={(val) => updateInputField(idx, { type: val })}
                                            hideClearButton
                                        />
                                    </View>
                                    <View style={styles.inputFieldCol}>
                                        <SelectInput
                                            title="Semantic"
                                            value={field.semantic || ''}
                                            options={INPUT_SEMANTIC_OPTIONS}
                                            onUpdate={(val) => updateInputField(idx, { semantic: val || undefined })}
                                        />
                                    </View>
                                </View>
                                <ToggleRow
                                    label="Required"
                                    value={field.required === true}
                                    onValueChange={(val) => updateInputField(idx, { required: val })}
                                />
                            </View>
                        ))}
                        <TouchableOpacity style={styles.addInputBtn} onPress={addInputField} testID="add-input-field">
                            <Unicon name="add-circle-outline" size={18} colorVariant="primary" />
                            <Text style={styles.addInputText}>Add input</Text>
                        </TouchableOpacity>
                    </View>
                )}
            </ScrollView>
        </SheetModal>
    );
};

const styles = StyleSheet.create((theme) => ({
    scroll: { flexShrink: 1 },
    groupLabel: {
        fontSize: 11,
        fontFamily: theme.fonts.bold,
        color: theme.colors.textSecondary,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        marginBottom: 6,
    },
    hintText: {
        fontSize: 12,
        color: theme.colors.textSecondary,
        lineHeight: 17,
        marginBottom: 12,
    },
    inputSchemaBlock: { marginTop: 8 },
    inputFieldRow: {
        backgroundColor: theme.colors.surfaceVariant,
        borderRadius: 10,
        padding: 10,
        marginBottom: 8,
    },
    inputFieldHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 4,
    },
    inputFieldIndex: {
        fontSize: 11,
        fontFamily: theme.fonts.bold,
        color: theme.colors.textSecondary,
        textTransform: 'uppercase',
    },
    inputFieldCols: { flexDirection: 'row', gap: 8 },
    inputFieldCol: { flex: 1 },
    addInputBtn: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingVertical: 8,
    },
    addInputText: {
        fontSize: 13,
        fontFamily: theme.fonts.bold,
        color: theme.colors.primary,
    },
}));

export default TriggerConfigSheet;
